import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DashboardContext } from '../../../context'
import AppDataWrapper from '../../../components/containers/AppDataWrapper'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ResourceItem from '../../../components/containers/ResourceItem'
import { CustomTabProps, FormatGridType, ResourceType, SortByTypes } from '../../../types'
import ArtifactContainer from '../../../components/artifacts/ArtifactContainer'
import TabContainer from '../../../components/containers/TabContainer'
import { Box, Button, NativeSelect, Typography } from '@mui/material'
import { SORT_OPTIONS, URLQUERY } from '../../../config'
import util from '../../../helpers/util.helper'
import ActionButtonsGroup from '../../../components/containers/ActionButtonsGroup'
import matchHelper from '../../../helpers/match.helper'

export default function LinkedResources() {
    const [searchParams, setSearchParams] = useSearchParams({})
    const context = useContext(DashboardContext)
    const [search, setSearch] = useState('')
    const [sortBy, setSortBy] = useState<SortByTypes>('name')
    const [orderAsc, setOrderAsc] = useState<boolean>(true)
    const [format, setFormat] = useState<FormatGridType>('list')
    const [target, setTarget] = useState<ResourceType | null>(null)

    const onSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSortBy(e.target.value as SortByTypes)
    }, [])

    const menuLinkedResources = useMemo(
        () => {
            let dataset = context?.appData.linkedResources?.filter(lr => lr.linkedMenus?.includes(context?.navBar.menuSelected?.id || 0)) || []
            if (search) {
                dataset = dataset.filter(item => matchHelper.validateMatch(item.label, search, matchHelper.matchParamsDefault) ||
                    matchHelper.validateMatch(item.description, search, matchHelper.matchParamsDefault) ||
                    matchHelper.validateMatch(item.versions[0]?.artifacts.reduce((prevVal, curVal) => `${prevVal}, ${curVal.name}|${curVal.type}`, ''), search, matchHelper.matchParamsDefault))
            }
            setTarget(null)
            return dataset.sort(util.sortResourceItems(sortBy, orderAsc))
        },
        [context?.appData.linkedResources, context?.navBar.menuSelected, search, sortBy, orderAsc]
    )
    const tabGroup = useMemo<CustomTabProps[]>(() => {
        let tabs: CustomTabProps[] = []
        if (target) {
            let artifacts = target.versions[0]?.artifacts
            artifacts.forEach(a => {
                tabs.push({
                    label: context?.getCatalog('ART')?.find(t => t.value === a.type)?.label || a.type,
                    panel: <div><ArtifactContainer appModule='resources' resource={target} artifact={a} /></div>
                })
            })
        }
        return tabs
    }, [menuLinkedResources, target])

    function clearTarget() {
        setSearchParams({})
        setTarget(null)
    }

    useEffect(() => {
        let idTarget = Number(searchParams.get(URLQUERY.target))
        if (idTarget && idTarget != null) {
            let findedTarget = menuLinkedResources.find(m => m.id === idTarget) || null
            setTarget(findedTarget ? findedTarget : null)
        }
    }, [searchParams, menuLinkedResources])

    return (
        <AppDataWrapper
            appendToolBar={<div className='data-grid w-100'>
                <div className='data-grid-item w-100 m-auto'>
                    <Typography variant='caption'>{menuLinkedResources.length} elementos</Typography>
                </div>
                <div className='data-grid-item w-100 m-l-auto'>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', marginLeft: 'auto' }}>
                        <NativeSelect size='small' variant='standard' value={sortBy} onChange={onSortChange} sx={{ padding: 0, fontSize: '0.8rem', marginLeft: 'auto' }}>
                            {SORT_OPTIONS.map(option => <option value={option.value} key={option.value} >{option.label}</option>)}
                        </NativeSelect>
                        <ActionButtonsGroup
                            items={[
                                { icon: orderAsc ? 'sort-amount-up' : 'sort-amount-down', action: () => setOrderAsc(!orderAsc), title: 'Ordenar por', style: { borderRadius: '0' } },
                                { icon: format === 'grid' ? 'list-ul' : 'grip-horizontal', action: () => setFormat(format === 'grid' ? 'list' : 'grid'), title: 'Ver por', style: { borderRadius: '0' } }
                            ]}
                        />
                    </Box>
                </div>
            </div>}
            withSearch={{
                onSearch: setSearch,
                search: search
            }}
        >
            <div className={`resource-container ${target && 'active'}`}>
                <div className={`resource-visor`}>
                    <div className='d-flex-center'>
                        <Typography variant='h6'>{target?.label}</Typography>
                        <Button
                            variant='contained'
                            color='error'
                            className='m-l-auto'
                            onClick={clearTarget}
                        >
                            <FontAwesomeIcon icon="times" />
                        </Button>
                    </div>
                    <br />
                    <Typography variant='body1'>{target?.description}</Typography>
                    <div className='data-grid'>
                        <div className='data-grid-item w-100'>
                            <Typography variant='caption'>{target?.updatedAt}</Typography>
                        </div>
                    </div>
                    <TabContainer tabs={tabGroup} />
                </div>
                <div className={`resource-${target ? 'grid' : format}`}>
                    {menuLinkedResources.map((resource) => <ResourceItem
                        key={resource.id}
                        item={resource}
                        format={target ? 'grid' : format}
                        onClick={() => setTarget(resource)}
                        isTarget={resource.id === target?.id}
                    />)}
                </div>
            </div>
        </AppDataWrapper>
    )
}
